import { useState, useEffect, type FormEvent } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import type { Cliente } from '@/lib/types'

const emptyCliente: Omit<Cliente, 'id'> = {
  empresa: '', contacto: '', telefono: '', email: '', ruc_dni: '', direccion: '',
}

export default function Clientes() {
  const { getAll, add, update, remove } = useSupabase()
  const [clientes, setClientes] = useState<Cliente[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<Cliente | null>(null)
  const [editing, setEditing] = useState<Cliente | null>(null)
  const [form, setForm] = useState(emptyCliente)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')

  async function fetchClientes() {
    try {
      const data = await getAll<Cliente>('clientes')
      setClientes(data)
    } catch { showToast('Error al cargar clientes', 'error') }
    finally { setLoading(false) }
  }

  useEffect(() => { fetchClientes() }, [])

  function openNew() {
    setEditing(null)
    setForm(emptyCliente)
    setModalOpen(true)
  }

  function openEdit(c: Cliente) {
    setEditing(c)
    setForm({ empresa: c.empresa, contacto: c.contacto ?? '', telefono: c.telefono ?? '', email: c.email ?? '', ruc_dni: c.ruc_dni ?? '', direccion: c.direccion ?? '' })
    setModalOpen(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.empresa.trim()) { showToast('El nombre de empresa es requerido', 'error'); return }
    setSaving(true)
    try {
      if (editing) {
        await update('clientes', { id: editing.id, ...form })
        showToast('Cliente actualizado', 'success')
      } else {
        await add('clientes', form)
        showToast('Cliente creado', 'success')
      }
      setModalOpen(false)
      await fetchClientes()
    } catch { showToast('Error al guardar cliente', 'error') }
    finally { setSaving(false) }
  }

  async function handleDelete() {
    if (!deleteConfirm) return
    try {
      await remove('clientes', deleteConfirm.id)
      showToast('Cliente eliminado', 'success')
      setDeleteConfirm(null)
      await fetchClientes()
    } catch { showToast('Error al eliminar', 'error') }
  }

  const filtered = clientes.filter(c =>
    [c.empresa, c.contacto, c.email, c.ruc_dni].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  )

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <div className="page-header"><h1>Clientes</h1></div>
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        Cargando clientes...
      </div>
    </div>
  )

  return (
    <div className="page-enter">
      <div className="page-header">
        <div>
          <h1>Clientes</h1>
          <p className="page-subtitle">{clientes.length} cliente{clientes.length !== 1 ? 's' : ''} registrado{clientes.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openNew} id="btn-new-client">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
          Nuevo Cliente
        </button>
      </div>

      {clientes.length > 4 && (
        <div style={{ marginBottom: '16px' }}>
          <input className="form-input" placeholder="Buscar por empresa, contacto, email o RUC..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} />
        </div>
      )}

      {clientes.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <h3>Sin clientes</h3>
          <p>Crea tu primer cliente para comenzar a cotizar.</p>
          <button className="btn btn-primary" onClick={openNew}>Crear Cliente</button>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Empresa</th><th>Contacto</th><th>Email</th><th>Teléfono</th><th>RUC / DNI</th><th style={{ width: 120 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600 }}>{c.empresa}</td>
                  <td>{c.contacto ?? '—'}</td>
                  <td>{c.email ?? '—'}</td>
                  <td>{c.telefono ?? '—'}</td>
                  <td>{c.ruc_dni ?? '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(c)} title="Editar">✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(c)} title="Eliminar">🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No se encontraron resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Cliente' : 'Nuevo Cliente'}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Empresa *</label>
            <input className="form-input" value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))} required autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Contacto</label>
              <input className="form-input" value={form.contacto ?? ''} onChange={e => setForm(f => ({ ...f, contacto: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Teléfono</label>
              <input className="form-input" value={form.telefono ?? ''} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">RUC / DNI</label>
              <input className="form-input" value={form.ruc_dni ?? ''} onChange={e => setForm(f => ({ ...f, ruc_dni: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Dirección</label>
            <input className="form-input" value={form.direccion ?? ''} onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : editing ? 'Actualizar' : 'Crear'}</button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Confirmar Eliminación">
        <p style={{ marginBottom: 8 }}>¿Estás seguro de eliminar al cliente <strong>{deleteConfirm?.empresa}</strong>?</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Esta acción no se puede deshacer.</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleDelete}>Eliminar</button>
        </div>
      </Modal>
    </div>
  )
}
